import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1461695008884-244cb4543d74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBhcmNoaXRlY3R1cmUlMjBnbGFzcyUyMGFic3RyYWN0fGVufDB8fHx8MTc5MTAzNTA0OHww&ixlib=rb-4.1.0&q=85";

const LINES = ["Your India GCC,", "engineered", "end to end."];

export default function Hero({ onNavigate }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);

  return (
    <section id="home" ref={ref} className="border-b border-line pt-24 md:pt-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 pb-16 md:grid-cols-12 md:pb-24">
        <div className="md:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-ink/60"
            data-testid="hero-overline"
          >
            Crestbridge — Financial and Strategy Execution
          </motion.p>
          <h1
            className="font-serif text-5xl font-medium leading-[0.95] tracking-tight md:text-7xl lg:text-[5.5rem]"
            data-testid="hero-headline"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{
                    duration: 1,
                    delay: 0.2 + i * 0.14,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  {i === 1 ? <em className="text-gold">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-ink/70 md:text-sm"
            data-testid="hero-subline"
          >
            Finance — Governance — Technology — Strategic Execution
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-6 font-serif text-xl italic tracking-tight text-ink md:text-2xl"
            data-testid="hero-tagline"
          >
            When Global Ambition meets <em className="not-italic text-gold">Local Execution.</em>
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="mt-6 max-w-md text-base leading-relaxed text-ink/70"
            data-testid="hero-copy"
          >
            We design, build and run Global Capability Centers in India — for
            companies that want ownership, not outsourcing.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <button
              onClick={() => onNavigate("#contact")}
              data-testid="hero-cta-start"
              className="border border-ink bg-transparent px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-ink hover:text-paper"
            >
              Start a conversation
            </button>
            <button
              onClick={() => onNavigate("#gcc-execution")}
              data-testid="hero-cta-model"
              className="flex items-center gap-2 border-b border-ink/30 px-1 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink/70 transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              See the model <ArrowDownRight className="h-4 w-4" />
            </button>
          </motion.div>
        </div>
        <div className="md:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden border border-ink/20"
          >
            <motion.img
              style={{ y }}
              src={HERO_IMG}
              alt="Modern corporate architecture"
              className="h-[420px] w-full scale-125 object-cover grayscale contrast-125 md:h-[560px]"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-mustard/15 mix-blend-multiply" />
          </motion.div>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">
            Mumbai — BKC · 19.0660° N, 72.8777° E
          </p>
        </div>
      </div>
    </section>
  );
}
